import { useCallback, useEffect, useMemo, useRef } from "react";

import { CodeChangesContext } from "./CodeChangesContext";
import Group from "./components/Group";
import styles from "./styles.module.css";
import { CodeChangesGroupNode } from "./types";

type CodeChangesProps = {
  changes: CodeChangesGroupNode;
  collapsedGroups: Set<string>;
  onGroupToggle: (collapsedGroups: Set<string>) => void;
  onResize?: (height: number) => void;
};

const CodeChanges = ({ changes, collapsedGroups, onGroupToggle, onResize }: CodeChangesProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const toggleGroup = useCallback(
    (id: string) => {
      const newSet = new Set(collapsedGroups);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      onGroupToggle?.(newSet);
    },
    [collapsedGroups, onGroupToggle]
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([entry]) => {
      onResize?.(entry.contentRect.height);
    });

    if (ref.current && onResize) resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, [onResize]);

  const contextValue = useMemo(
    () => ({ collapsedGroups, toggleGroup }),
    [collapsedGroups, toggleGroup]
  );

  return (
    <CodeChangesContext.Provider value={contextValue}>
      <div className={styles.codeChangesWrapper} ref={ref}>
        {changes.children.map((child) => (
          <Group key={child.id} item={child} />
        ))}
      </div>
    </CodeChangesContext.Provider>
  );
};

export default CodeChanges;
